// BEGIN FILE: src/components/DashaFocusOverview.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { Box, Button, Chip, IconButton, Tooltip, Typography } from '@mui/joy';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  classifyFunctionalForLagna,
  getGrahaLongitudesAndMotionSidereal,
  getAscendantSignAndNakshatra,
  getWholeSignCuspsSidereal,
  assignPlanetsToHouses,
  getRahuKetuSidereal
} from '../utils/astroEngine';
import { getAyanamsaProvider, type AyanamsaProvider } from '../utils/ayanamsa';
import { computeHouseScoresWithLordsAndAspects, type AspectEdge } from '../utils/houseScores';
import { supabase } from '../utils/supabaseClient';

/* =========================
   Types & constants
   ========================= */

type DashaPlanet =
  | 'Sun' | 'Moon' | 'Mars' | 'Mercury' | 'Jupiter' | 'Venus' | 'Saturn' | 'Rahu' | 'Ketu';
type SevenPlanet = Exclude<DashaPlanet, 'Rahu'|'Ketu'>;
type FuncMap = Record<SevenPlanet, 'benefic'|'malefic'|'neutral'>;

type DashaBand = { lord: DashaPlanet; start: string; end: string };

type Props = {
  isoDate: string;
  latitude: number;
  longitude: number;
  ascSign: number; // 1..12
  ayanamsa?: AyanamsaProvider;
  blendedPct?: Partial<Record<DashaPlanet, number>>; // optional: planet blended % (signed, -100..+100)
  externalHouseScores?: Record<number, number> | null; // optional: use external house scores instead of computing internally
  externalAspects?: Array<{ from: string; toHouse: number }> | null; // optional: use external aspects instead of computing
  center?: DateTime; // optional: override date for dasha calculation
  onAnalyseCurrentTime?: () => void;
  isAdmin?: boolean;
  remainingAnalyses?: number;
};

/** Vimśottarī canonical order & year lengths */
type DashaLord = 'Ketu'|'Venus'|'Sun'|'Moon'|'Mars'|'Rahu'|'Jupiter'|'Saturn'|'Mercury';
const DASHA_ORDER: DashaLord[] = ['Ketu','Venus','Sun','Moon','Mars','Rahu','Jupiter','Saturn','Mercury'];
const DASHA_YEARS: Record<DashaLord, number> = {
  Ketu:7, Venus:20, Sun:6, Moon:10, Mars:7, Rahu:18, Jupiter:16, Saturn:19, Mercury:17
};

const DASHA_YEAR_DAYS = 365.25636;
const DAY_MS = 86400000;
const YEAR_MS = DASHA_YEAR_DAYS * DAY_MS;

const NAK_LEN_DEG = 360 / 27; // 13°20'
const SEVEN: SevenPlanet[] = ['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn'];
const isSeven = (p: DashaPlanet): p is SevenPlanet => (SEVEN as string[]).includes(p);

const NAK_LORDS: DashaLord[] = Array.from({ length: 27 }, (_, i) => DASHA_ORDER[i % 9]);

// House rulership by sign
const SIGN_LORD: Record<number, DashaPlanet> = {
  1:'Mars',2:'Venus',3:'Mercury',4:'Moon',5:'Sun',6:'Mercury',
  7:'Venus',8:'Mars',9:'Jupiter',10:'Saturn',11:'Saturn',12:'Jupiter',
};

// Parāśari special aspects (houses away)
const PARASHARI_OFFSETS: Record<DashaPlanet, number[]> = {
  Sun:[7], Moon:[7], Mercury:[7], Venus:[7],
  Mars:[4,7,8], Jupiter:[5,7,9], Saturn:[3,7,10],
  Rahu:[5,7,9], Ketu:[5,7,9]
};

const HOUSE_AREAS: Record<number, string> = {
  1:'Self, Vitality, Body',
  2:'Wealth, Family, Speech',
  3:'Courage, Siblings, Effort',
  4:'Home, Mother, Comforts',
  5:'Children, Creativity, Intellect',
  6:'Enemies, Debts, Health',
  7:'Marriage, Partnerships, Public',
  8:'Longevity, Transformations, Secrets',
  9:'Fortune, Dharma, Higher learning',
 10:'Career, Status, Authority',
 11:'Gains, Networks, Aspirations',
 12:'Losses, Expenditure, Moksha',
};

/* =========================
   Vimshottari helpers
   ========================= */

function getMahadasha(moonLon: number, birthISO: string): DashaBand[] {
  const nak = Math.floor((moonLon % 360) / NAK_LEN_DEG);
  const lord = NAK_LORDS[nak];
  const offset = (moonLon % NAK_LEN_DEG) / NAK_LEN_DEG;
  const birthDt = DateTime.fromISO(birthISO, { zone: 'utc' });
  const firstLord = lord;
  const firstYears = DASHA_YEARS[firstLord];
  const balanceYears = firstYears * (1 - offset);
  let cursor = birthDt.plus({ milliseconds: balanceYears * YEAR_MS });
  const bands: DashaBand[] = [{ lord: firstLord, start: birthDt.toISO()!, end: cursor.toISO()! }];
  let lordIndex = (DASHA_ORDER.indexOf(firstLord) + 1) % 9;
  while (bands.length < 200) {
    const l = DASHA_ORDER[lordIndex];
    const yearLen = DASHA_YEARS[l];
    const next = cursor.plus({ milliseconds: yearLen * YEAR_MS });
    bands.push({ lord: l, start: cursor.toISO()!, end: next.toISO()! });
    cursor = next;
    lordIndex = (lordIndex + 1) % 9;
  }
  return bands;
}

function buildAntardasha(mdBand: DashaBand): DashaBand[] {
  const startMs = DateTime.fromISO(mdBand.start).toMillis();
  const endMs = DateTime.fromISO(mdBand.end).toMillis();
  const totalMs = endMs - startMs;
  const mdIdx = DASHA_ORDER.indexOf(mdBand.lord);
  const bands: DashaBand[] = [];
  let c = startMs;
  for (let i = 0; i < 9; i++) {
    const ad = DASHA_ORDER[(mdIdx + i) % 9];
    const fraction = DASHA_YEARS[ad] / DASHA_YEARS[mdBand.lord];
    const segMs = fraction * totalMs;
    const s = DateTime.fromMillis(c, { zone: 'utc' }).toISO()!;
    c += segMs;
    const e = DateTime.fromMillis(c, { zone: 'utc' }).toISO()!;
    bands.push({ lord: ad, start: s, end: e });
  }
  return bands;
}

function buildPratyantardasha(adBand: DashaBand): DashaBand[] {
  const startMs = DateTime.fromISO(adBand.start).toMillis();
  const endMs = DateTime.fromISO(adBand.end).toMillis();
  const totalMs = endMs - startMs;
  const adIdx = DASHA_ORDER.indexOf(adBand.lord);
  const bands: DashaBand[] = [];
  let c = startMs;
  for (let i = 0; i < 9; i++) {
    const pd = DASHA_ORDER[(adIdx + i) % 9];
    const fraction = DASHA_YEARS[pd] / DASHA_YEARS[adBand.lord];
    const segMs = fraction * totalMs;
    const s = DateTime.fromMillis(c, { zone: 'utc' }).toISO()!;
    c += segMs;
    const e = DateTime.fromMillis(c, { zone: 'utc' }).toISO()!;
    bands.push({ lord: pd, start: s, end: e });
  }
  return bands;
}

function buildSubdasha(pdBand: DashaBand): DashaBand[] {
  const startMs = DateTime.fromISO(pdBand.start).toMillis();
  const endMs = DateTime.fromISO(pdBand.end).toMillis();
  const totalMs = endMs - startMs;
  const pdIdx = DASHA_ORDER.indexOf(pdBand.lord);
  const bands: DashaBand[] = [];
  let c = startMs;
  for (let i = 0; i < 9; i++) {
    const sd = DASHA_ORDER[(pdIdx + i) % 9];
    const fraction = DASHA_YEARS[sd] / DASHA_YEARS[pdBand.lord];
    const segMs = fraction * totalMs;
    const s = DateTime.fromMillis(c, { zone: 'utc' }).toISO()!;
    c += segMs;
    const e = DateTime.fromMillis(c, { zone: 'utc' }).toISO()!;
    bands.push({ lord: sd, start: s, end: e });
  }
  return bands;
}

function pickCurrent<T extends DashaBand>(bands: T[], nowMs: number): T | null {
  for (const b of bands) {
    const s = DateTime.fromISO(b.start).toMillis();
    const e = DateTime.fromISO(b.end).toMillis();
    if (nowMs >= s && nowMs < e) return b;
  }
  return null;
}

function fmtBlended(lord: DashaPlanet, blendedPct?: Partial<Record<DashaPlanet, number>>) {
  if (!blendedPct || blendedPct[lord] == null) return '';
  const val = blendedPct[lord]!;
  const sign = val >= 0 ? '+' : '';
  return ` ${sign}${val.toFixed(0)}%`;
}

function fmtEndInclusive(isoEnd: string) {
  return DateTime.fromISO(isoEnd).minus({ days: 1 }).toFormat('dd/LL/yyyy');
}

function humanYmdDuration(isoStart: string, isoEnd: string): string {
  const s = DateTime.fromISO(isoStart);
  const e = DateTime.fromISO(isoEnd);
  const diff = e.diff(s, ['years', 'months', 'days']);
  const y = Math.floor(diff.years);
  const m = Math.floor(diff.months);
  const d = Math.floor(diff.days);
  const parts: string[] = [];
  if (y > 0) parts.push(`${y}y`);
  if (m > 0) parts.push(`${m}m`);
  if (d > 0) parts.push(`${d}d`);
  return parts.join(' ') || '0d';
}

function colorFor(lord: DashaPlanet, funcMap: FuncMap): string {
  if (!isSeven(lord)) return '#d4b106'; // nodes
  const fc = funcMap[lord];
  if (fc === 'benefic') return '#21a366';
  if (fc === 'malefic') return '#c9302c';
  return '#d4b106';
}

/* =========================
   Main Component
   ========================= */

function DashaFocusOverview({
  isoDate,
  latitude,
  longitude,
  ascSign,
  ayanamsa,
  blendedPct,
  externalHouseScores,
  externalAspects,
  center,
  onAnalyseCurrentTime,
  isAdmin,
  remainingAnalyses,
}: Props) {
  const [housesReloadKey, setHousesReloadKey] = useState(0);
  const [housesLoadState, setHousesLoadState] = useState<{loading:boolean; count:number; error?:string}>({
    loading: true,
    count: 0
  });
  const [housesData, setHousesData] = useState<Record<number, { relations?: string; body_parts?: string; professions?: string }>>({});

  // Use provided center date or fallback to isoDate
  const effectiveCenter = center || DateTime.fromISO(isoDate, { zone: 'utc' });
  const nowMs = effectiveCenter.toMillis();

  // Compute natal chart
  const natal = useMemo(() => {
    try {
      const provider = ayanamsa ?? getAyanamsaProvider('lahiri');
      const g = getGrahaLongitudesAndMotionSidereal({ isoDate, latitude, longitude }, provider) as any;
      const nodes = getRahuKetuSidereal({ isoDate, latitude, longitude }, provider) as any;
      const longs: Partial<Record<DashaPlanet, number>> = {
        Sun: g?.Sun?.lon, Moon: g?.Moon?.lon, Mars: g?.Mars?.lon, Mercury: g?.Mercury?.lon,
        Jupiter: g?.Jupiter?.lon, Venus: g?.Venus?.lon, Saturn: g?.Saturn?.lon,
        Rahu: nodes?.Rahu, Ketu: nodes?.Ketu,
      };
      const asc = getAscendantSignAndNakshatra(isoDate, latitude, longitude);
      const cusps = getWholeSignCuspsSidereal(asc.siderealLongitudeDeg);
      const placements = assignPlanetsToHouses(cusps, longs as any) as Record<DashaPlanet, { house: number }>;
      return { asc, cusps, placements, longs };
    } catch {
      return null;
    }
  }, [isoDate, latitude, longitude, ayanamsa]);

  // Compute transit chart (for "today" column) - use natal cusps to see which natal houses transiting planets occupy
  const transit = useMemo(() => {
    if (!natal?.cusps) return null;
    try {
      const today = DateTime.utc();
      const iso = today.toISO();
      const provider = ayanamsa ?? getAyanamsaProvider('lahiri');
      const g = getGrahaLongitudesAndMotionSidereal({ isoDate: iso!, latitude, longitude }, provider) as any;
      const nodes = getRahuKetuSidereal({ isoDate: iso!, latitude, longitude }, provider) as any;
      const longs: Partial<Record<DashaPlanet, number>> = {
        Sun: g?.Sun?.lon, Moon: g?.Moon?.lon, Mars: g?.Mars?.lon, Mercury: g?.Mercury?.lon,
        Jupiter: g?.Jupiter?.lon, Venus: g?.Venus?.lon, Saturn: g?.Saturn?.lon,
        Rahu: nodes?.Rahu, Ketu: nodes?.Ketu,
      };
      // Use NATAL cusps to calculate which natal houses transiting planets occupy
      const placements = assignPlanetsToHouses(natal.cusps, longs as any) as Record<DashaPlanet, { house: number }>;
      return { placements };
    } catch {
      return null;
    }
  }, [latitude, longitude, ayanamsa, natal]);

  // Load house metadata from Supabase (with permanent localStorage caching)
  useEffect(() => {
    const cacheKey = 'houses_metadata_v1';
    const cachedStr = localStorage.getItem(cacheKey);
    let cached: any[] | null = null;
    
    if (cachedStr) {
      try {
        const parsed = JSON.parse(cachedStr);
        const age = Date.now() - (parsed.timestamp || 0);
        const maxAge = 365 * 24 * 60 * 60 * 1000; // 365 days
        if (age < maxAge) {
          cached = parsed.data;
        }
      } catch (e) {
        // Invalid cache
      }
    }
    
    if (cached) {
      // Use cached data
      const map: Record<number, { relations?: string; body_parts?: string; professions?: string }> = {};
      cached.forEach((h: any) => {
        if (typeof h?.id === 'number') {
          map[h.id] = { relations: h.relations, body_parts: h.body_parts, professions: h.professions };
        }
      });
      setHousesData(map);
      setHousesLoadState({ loading: false, count: Object.keys(map).length });
    } else {
      // Fetch from Supabase
      setHousesLoadState({ loading: true, count: 0 });
      supabase.from('houses').select('id, relations, body_parts, professions').order('id', { ascending: true })
        .then(({ data, error }) => {
          if (error) {
            setHousesLoadState({ loading: false, count: 0, error: error.message });
            return;
          }
          if (data && Array.isArray(data)) {
            const map: Record<number, { relations?: string; body_parts?: string; professions?: string }> = {};
            data.forEach((h: any) => {
              if (typeof h?.id === 'number') {
                map[h.id] = { relations: h.relations, body_parts: h.body_parts, professions: h.professions };
              }
            });
            setHousesData(map);
            setHousesLoadState({ loading: false, count: Object.keys(map).length });
            // Save to localStorage cache
            localStorage.setItem(cacheKey, JSON.stringify({ data, timestamp: Date.now() }));
          } else {
            setHousesLoadState({ loading: false, count: 0, error: 'No data returned' });
          }
        });
    }
  }, [housesReloadKey]);

  // Compute functional classification
  const funcMap: FuncMap = useMemo(() => {
    const ascSignIndex = (natal?.asc as any)?.ascendantSignIndex || ascSign;
    return classifyFunctionalForLagna(ascSignIndex) as any;
  }, [natal, ascSign]);

  // Build natal Parashara aspects as edges
  const natalAspectEdges = useMemo<AspectEdge[] | null>(() => {
    if (externalAspects && Array.isArray(externalAspects)) {
      return externalAspects.map(a => ({ from: a.from as DashaPlanet, toHouse: a.toHouse }));
    }
    
    if (!natal?.placements) return null;
    const edges: AspectEdge[] = [];
    (['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn','Rahu','Ketu'] as DashaPlanet[])
      .forEach((p) => {
        const h = natal.placements?.[p]?.house;
        if (!h) return;
        (PARASHARI_OFFSETS[p] ?? [7]).forEach((off) => {
          const to = ((h - 1 + (off - 1)) % 12) + 1;
          edges.push({ from: p as any, toHouse: to });
        });
      });
    return edges;
  }, [externalAspects, natal]);

  // Compute or use external house scores
  const houseScores = useMemo<Record<number, number>>(() => {
    if (externalHouseScores) {
      return externalHouseScores;
    }
    
    if (!natal?.placements || !natal?.asc || !natalAspectEdges) return {};
    
    const planetPct: Required<Record<DashaPlanet, number>> = {
      Sun: 0, Moon: 0, Mars: 0, Mercury: 0, Jupiter: 0, Venus: 0, Saturn: 0, Rahu: 0, Ketu: 0,
      ...(blendedPct as any || {})
    } as any;
    const ascIdx = (natal.asc as any)?.ascendantSignIndex || ascSign;
    
    try {
      const scoresArray = computeHouseScoresWithLordsAndAspects(planetPct as any, natal.placements as any, natalAspectEdges as any, ascIdx, { clampTo: 999 });
      const scoresMap: Record<number, number> = {};
      for (let h = 1; h <= 12; h++) {
        scoresMap[h] = scoresArray[h - 1] ?? 0;
      }
      return scoresMap;
    } catch {
      return {};
    }
  }, [externalHouseScores, natal, natalAspectEdges, blendedPct, ascSign]);

  // Build dasha bands
  const mdBands = useMemo(() => {
    if (!natal?.longs?.Moon) return [];
    return getMahadasha(natal.longs.Moon, isoDate);
  }, [natal, isoDate]);
  const mdAtCenter = pickCurrent(mdBands, nowMs);
  const adBands = mdAtCenter ? buildAntardasha(mdAtCenter) : [];
  const adAtCenter = pickCurrent(adBands, nowMs);
  const pdBands = adAtCenter ? buildPratyantardasha(adAtCenter) : [];
  const pdAtCenter = pickCurrent(pdBands, nowMs);
  const sdBands = pdAtCenter ? buildSubdasha(pdAtCenter) : [];
  const sdAtCenter = pickCurrent(sdBands, nowMs);

  // Helper: label house with score
  const houseLabel = (h: number) => `H${h}`;
  const labelWithScore = (h: number) => {
    const score = houseScores[h];
    const formattedScore = typeof score === 'number' ? score.toFixed(1) : '—';
    const areas = HOUSE_AREAS[h] || '';
    return `${houseLabel(h)} (${areas}) [${formattedScore}]`;
  };

  // Helper: build focus row data
  const focusRow = (tag: string, band: DashaBand | null) => {
    if (!band || !natal) return null;
    const p = band.lord;
    const companions: DashaPlanet[] = [];
    const myHouse = natal.placements?.[p]?.house;
    if (myHouse) {
      for (const [planet, plc] of Object.entries(natal.placements)) {
        if (planet === p) continue;
        if (plc.house === myHouse) {
          companions.push(planet as DashaPlanet);
        }
      }
    }
    
    // Get ascendant sign for house calculation
    const ascSignIndex = (natal.asc as any)?.ascendantSignIndex || ascSign;
    
    const ruled: number[] = [];
    for (let sign = 1; sign <= 12; sign++) {
      if (SIGN_LORD[sign] === p) {
        const houseNum = ((ascSignIndex + (sign - 1) - 1) % 12) + 1;
        ruled.push(houseNum);
      }
    }
    const occNatal = natal.placements?.[p]?.house || 0;
    const offsets = PARASHARI_OFFSETS[p] || [7];
    const aspEmpty: number[] = [];
    if (occNatal) {
      for (const off of offsets) {
        const targetH = ((occNatal + off - 1 - 1) % 12) + 1;
        const occupied = Object.values(natal.placements).some(plc => plc.house === targetH);
        if (!occupied) aspEmpty.push(targetH);
      }
    }
    const ruledStr = ruled.length ? ruled.map(h => labelWithScore(h)).join(', ') : '—';
    const occStr = occNatal ? labelWithScore(occNatal) : '—';
    const aspStr = aspEmpty.length ? aspEmpty.map(h => labelWithScore(h)).join(', ') : '—';
    
    const transitingInOccupiedHouse: string[] = [];
    if (occNatal && transit) {
      for (const [planet, plc] of Object.entries(transit.placements)) {
        if (plc.house === occNatal) transitingInOccupiedHouse.push(planet);
      }
    }
    
    const start = DateTime.fromISO(band.start).toFormat('dd/LL/yyyy');
    const end = fmtEndInclusive(band.end);
    const duration = humanYmdDuration(band.start, band.end);
    const transitingWhere = transit?.placements?.[p]?.house ? houseLabel(transit.placements[p]!.house) : '—';
    const whoInOccHouse = transitingInOccupiedHouse.length 
      ? transitingInOccupiedHouse.join(', ') 
      : (occNatal ? `No planet transiting in ${houseLabel(occNatal)}` : '—');
    const focusSet = new Set<number>([...ruled, ...(occNatal ? [occNatal] : []), ...aspEmpty]);
    return { 
      tag, 
      planet: p, 
      companions, 
      start, 
      end, 
      duration, 
      ruledStr, 
      occStr, 
      aspStr, 
      transitingWhere, 
      whoInOccHouse, 
      focusHouses: Array.from(focusSet), 
      ruled, 
      occNatal: occNatal || 0, 
      aspEmpty 
    } as any;
  };

  // Memoize table rows
  const tableRows = useMemo(() => {
    return [
      focusRow('MD', mdAtCenter),
      focusRow('AD', adAtCenter),
      focusRow('PD', pdAtCenter),
      focusRow('SD', sdAtCenter),
    ].filter(Boolean) as Array<ReturnType<typeof focusRow>>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mdAtCenter, adAtCenter, pdAtCenter, sdAtCenter, natal, transit, housesData, houseScores]);

  return (
    <Box sx={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>
      {/* Analyse My Current Time button — top of Dasha Focus Overview */}
      {onAnalyseCurrentTime && (
        <Box
          sx={{
            mb: 1.5,
            p: { xs: 1.5, sm: 2 },
            borderRadius: 'lg',
            background: 'linear-gradient(135deg, #5c6bc0 0%, #7b1fa2 100%)',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
            <Typography level="title-sm" sx={{ color: 'white', fontWeight: 700, mb: 0.25 }}>
              ⚡ Analyse My Current Time
            </Typography>
            <Typography level="body-xs" sx={{ color: 'rgba(255,255,255,0.82)' }}>
              6-week forecast · Moon tracking · Life area scores · 3-month summary
            </Typography>
            {!isAdmin && remainingAnalyses !== undefined && (
              <Typography level="body-xs" sx={{ color: 'rgba(255,255,255,0.65)', mt: 0.25 }}>
                {remainingAnalyses > 0
                  ? `${remainingAnalyses} analyse${remainingAnalyses === 1 ? '' : 's'} remaining this month`
                  : 'Monthly quota reached — upgrade for more'}
              </Typography>
            )}
          </Box>
          <Button
            variant="solid"
            size="sm"
            onClick={onAnalyseCurrentTime}
            startDecorator={<span style={{ fontSize: '1rem' }}>🔮</span>}
            sx={{
              flexShrink: 0,
              bgcolor: 'white',
              color: '#5c6bc0',
              fontWeight: 700,
              fontSize: '0.8rem',
              whiteSpace: 'nowrap',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.9)', transform: 'translateY(-1px)' },
            }}
          >
            Analyse Now
          </Button>
        </Box>
      )}

      <Box sx={{ p: 1, borderRadius: 'md', border: '1px solid var(--joy-palette-neutral-outlinedBorder)', bgcolor: 'background.surface' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography level="title-sm" sx={{ fontWeight: 700, color: '#b83050', fontFamily: '"Cinzel", "Garamond", serif' }}>
            Dasha Focus Overview (Compact)
          </Typography>
          <Tooltip 
            title="Detailed breakdown of current planetary periods (MD/AD/PD/SD). Shows which houses each dasha planet rules, occupies, and aspects - revealing which life areas are activated now. Transit column shows current sky positions reinforcing or challenging these themes." 
            placement="top" 
            arrow 
            variant="soft" 
            size="sm"
            sx={{ maxWidth: 280, p: 1, fontSize: '0.875rem', lineHeight: 1.5 }}
          >
            <IconButton size="sm" variant="plain" color="primary" sx={{ minWidth: 20, minHeight: 20, padding: '2px' }}>
              <HelpOutlineIcon sx={{ fontSize: '0.95rem' }} />
            </IconButton>
          </Tooltip>
        </Box>
        
        {/* House meta status */}
        {housesLoadState.loading && (
          <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Loading house meta…</div>
        )}
        {!housesLoadState.loading && housesLoadState.count < 12 && (
          <div style={{ fontSize: 12, color: '#8a6d3b', marginBottom: 6, display:'flex', alignItems:'center', gap:8 }}>
            <span>
              House meta unavailable{housesLoadState.error ? ` — ${housesLoadState.error}` : ''}. Ensure Supabase env is set and anon read is allowed for the houses table.
            </span>
            <Button size="sm" variant="outlined" onClick={() => setHousesReloadKey(k => k + 1)}>Retry</Button>
          </div>
        )}
        
        <Box sx={{ 
          overflowX: 'auto',
          overflowY: 'visible',
          maxWidth: '100%',
          '&::-webkit-scrollbar': {
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'var(--joy-palette-neutral-100)',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'var(--joy-palette-neutral-400)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'var(--joy-palette-neutral-500)',
          },
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 550, fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ textAlign:'left', padding:'6px 8px', borderBottom:'1px solid #ddd', minWidth: '140px', verticalAlign: 'top' }}>Level • Planet • Period</th>
                <th style={{ textAlign:'left', padding:'6px 8px', borderBottom:'1px solid #ddd' }}>Rules • Occupies • Aspects</th>
                <th style={{ textAlign:'left', padding:'6px 8px', borderBottom:'1px solid #ddd', minWidth: '90px', maxWidth: '110px', wordWrap: 'break-word' }}>Transiting in Occupied House</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                if (!tableRows.length) return (
                  <tr><td colSpan={3} style={{ padding:'8px 10px' }}>No dasha data.</td></tr>
                );
                
                // Build frequency map for highlighting
                const freq: Record<number, number> = {};
                tableRows.forEach((rr:any) => {
                  (rr.ruled || []).forEach((h:number) => { freq[h] = (freq[h]||0) + 1; });
                  if (rr.occNatal) freq[rr.occNatal] = (freq[rr.occNatal]||0) + 1;
                  (rr.aspEmpty || []).forEach((h:number) => { freq[h] = (freq[h]||0) + 1; });
                });
                
                const renderList = (hs:number[]) => (
                  hs.map((h, i) => (
                    <span key={`h-${h}-${i}`} style={{ fontWeight: (freq[h]||0) >= 2 ? 700 : 400 }}>
                      {labelWithScore(h)}{i < hs.length - 1 ? ', ' : ''}
                    </span>
                  ))
                );
                
                const renderOccupies = (r: any) => {
                  if (r.occStr === '—') return '—';
                  const isBold = (freq[r.occNatal]||0) >= 2;
                  // Split occStr: first part is "H{n} (life areas) [score]", rest is metadata
                  const parts = r.occStr.split(' • ');
                  if (parts.length === 0) return r.occStr;
                  const houseLabel = parts[0]; // e.g., "H4 (Home, Mother, Comforts) [-53.0]"
                  const metadata = parts.slice(1).join(' • '); // e.g., "Relations: ... • Body: ... • Career: ..."
                  return (
                    <>
                      <span style={{ fontWeight: isBold ? 700 : 400 }}>{houseLabel}</span>
                      {metadata && <span> • {metadata}</span>}
                    </>
                  );
                };

                return tableRows.map((r:any, idx:number) => (
                  <tr key={`focus-${idx}`}>
                    <td style={{ padding:'5px 8px', borderBottom:'1px solid rgba(212,175,55,0.2)', verticalAlign: 'top' }}>
                      {/* Level and Planet */}
                      <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom: 4 }}>
                        <strong style={{ fontSize: 11 }}>{r!.tag}</strong>:
                        <Chip size="sm" variant="solid" sx={{ bgcolor: colorFor(r!.planet, funcMap), color: '#fff', px: 0.6, py: 0, fontSize: 11 }}>
                          {r!.planet}
                        </Chip>
                        {blendedPct && (blendedPct as any)[r!.planet as any] != null && (
                          <span style={{ fontSize: 10, color: '#6d5c5c' }}>
                            {fmtBlended(r!.planet as any, blendedPct as any)}
                          </span>
                        )}
                      </div>
                      
                      {/* Companions */}
                      {r!.companions.length > 0 && (
                        <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:4, paddingLeft:8 }}>
                          {r!.companions.map((comp: DashaPlanet) => (
                            <div key={comp} style={{ display:'flex', alignItems:'center', gap:3 }}>
                              <span style={{ color: colorFor(comp, funcMap), fontWeight: 600, fontSize: 11 }}>
                                {comp}
                              </span>
                              {blendedPct && (blendedPct as any)[comp as any] != null && (
                                <span style={{ fontSize: 9, color: '#8e7a7a' }}>
                                  {fmtBlended(comp as any, blendedPct as any)}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Period dates and duration */}
                      <div style={{ fontSize: 10, color: '#5a4a4a', marginBottom: 3 }}>
                        <div>{r!.start} → {r!.end}</div>
                        <div style={{ color: '#8e7a7a' }}>({r!.duration})</div>
                      </div>
                      
                      {/* Transit info */}
                      <div style={{ fontSize: 10, color: '#6d5c5c', paddingTop: 3, borderTop: '1px solid rgba(212,175,55,0.15)' }}>
                        <strong>Transit:</strong> {r!.transitingWhere}
                      </div>
                    </td>
                    
                    <td style={{ padding:'5px 8px', borderBottom:'1px solid rgba(212,175,55,0.2)', verticalAlign: 'top', fontSize: 11 }}>
                      <div style={{ marginBottom: 4 }}><strong>Rules:</strong> {r!.ruled && r!.ruled.length > 0 ? renderList(r.ruled) : '—'}</div>
                      <div style={{ marginBottom: 4 }}><strong>Occupies:</strong> {renderOccupies(r)}</div>
                      <div><strong>Aspects:</strong> {r!.aspEmpty && r!.aspEmpty.length > 0 ? renderList(r.aspEmpty) : '—'}</div>
                    </td>
                    
                    <td style={{ padding:'5px 8px', borderBottom:'1px solid rgba(212,175,55,0.2)', verticalAlign: 'top', fontSize: 11, wordWrap: 'break-word', maxWidth: '110px' }}>
                      {r!.whoInOccHouse}
                    </td>
                  </tr>
                ));
              })()}
            </tbody>
          </table>
        </Box>
        <div style={{ fontSize: 11, color:'#7a6060', marginTop: 6, fontStyle: 'italic' }}>
          Note: Houses in bold appear multiple times across dasha levels. Scroll horizontally to view all columns.
        </div>
      </Box>
    </Box>
  );
}

export default React.memo(DashaFocusOverview);
// END FILE: src/components/DashaFocusOverview.tsx
